import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, ExternalLink } from 'react-feather';
import { enqueueSnackbar } from 'notistack';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  useTheme,
} from '@mui/material';

// Define role hierarchy for sorting (lower number = higher priority)
const roleOrder: Record<string, number> = {
  admin: 1,
  editor: 2,
  viewer: 3,
  limited_view: 4,
  undefined: 999,
};
interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  magicLoginLink: string;
}
interface AdminUsersPanelProps {
  users: User[];
}

export const AdminUsersPanel = ({ users }: AdminUsersPanelProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Sort users by role, then by name
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const roleA = roleOrder[(a.role || 'undefined').toLowerCase()];
      const roleB = roleOrder[(b.role || 'undefined').toLowerCase()];

      if (roleA !== roleB) {
        return roleA - roleB;
      }

      // If same role, sort by first name, then last name
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [users]);

  const handleRowClick = (user: User) => {
    navigate(`/admin/users/${user.id}`);
  };

  const handleCopyEmail = (
    event: React.MouseEvent<HTMLButtonElement>,
    email: string
  ) => {
    event.stopPropagation(); // Prevent row click
    navigator.clipboard.writeText(email);
    enqueueSnackbar(`Copied ${email} to clipboard`, {
      variant: 'success',
    });
  };

  return (
    <Box sx={{ margin: theme.spacing(2) }}>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Impersonate User</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((user) => (
              <TableRow
                key={user.id}
                onClick={() => handleRowClick(user)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <TableCell>
                  {user.firstName} {user.lastName}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {user.email}
                    <IconButton
                      size="small"
                      onClick={(e) => handleCopyEmail(e, user.email || '')}
                      sx={{ padding: 0.5 }}
                    >
                      <Copy size={16} />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>
                  {user.role}
                </TableCell>
                <TableCell>
                  <IconButton
                    href={user.magicLoginLink}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                      event.stopPropagation();
                    }}
                  >
                    <ExternalLink />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
